import {
  isResourceTypeFilterOptionActive,
  removeResourceTypeFilters,
} from '@macmillan-learning/gradebook-shared';
import dayjs from '../../../../utils/dayjs';
import PropTypes from 'prop-types';
import React from 'react';
import { connect, useDispatch } from 'react-redux';

import {
  getGradebook,
  getGradebookInstructor,
  setGradebookExceptionGroups,
  setGradebookFilterTypes,
  updateAllAverages,
  updateGradebook,
} from '../../../../actions/gradebook';
import { useExceptionGroupsQuery } from '../../../../core/lms';
import {
  courseIdSelector,
  exceptionGroupsSelector,
  filterTypesSelector,
  isGradebookV2ActiveSelector,
  searchDateFromSelector,
  searchDateToSelector,
} from '../../../../selectors/search-gradebook';
import { getAllResourceTypeFilterOptions } from '../../../../utils/util';
import MLButton from '../../../MLButton/MLButton';
import MLIcon from '../../../MLIcon/MLIcon';
import styles from '../../InstructorGradebook/InstructorGradebook.module.css';

function FilterHeader({
  courseId,
  exceptionGroups,
  resourceTypes,
  searchDateFrom,
  searchDateTo,
  innerRef,
  isGradebookV2Active,
}) {
  const dispatch = useDispatch();
  const resourceTypeFilterOptions = getAllResourceTypeFilterOptions();
  const { data: exceptionGroupOptions } = useExceptionGroupsQuery(courseId);

  const removeAllFilters = () => {
    dispatch(
      updateGradebook({
        exceptionGroups: [],
        filterTypes: [],
        searchDateFrom: null,
        searchDateTo: null,
      }),
    );
    if (isGradebookV2Active) {
      dispatch(
        getGradebookInstructor(courseId, {
          exceptionGroupIds: [],
          filterTypes: [],
        }),
      );
    } else {
      dispatch(
        getGradebook(courseId, { exceptionGroups: [], filterTypes: [] }),
      );
    }
    dispatch(updateAllAverages());
  };

  // Date Range Filter
  const renderActiveDateRangeFilterButton = () => {
    const DATE_FORMAT = 'LL';
    return (
      <div
        className={styles.selectedFilterType}
        key="date-range-badge"
        data-test-id="date_range_filter"
      >
        <div className={styles.selectedFilterTypeText}>
          {`${dayjs(searchDateFrom).format(DATE_FORMAT)}-${dayjs(
            searchDateTo,
          ).format(DATE_FORMAT)}`}
        </div>
        <MLButton
          className={styles.selectedFilterButton}
          testId="clear_date_range_filter"
          onClick={() => {
            clearDateRangeFilter();
          }}
        >
          <MLIcon type="x" className={styles.selectedFilterTypeIcon} />
        </MLButton>
      </div>
    );
  };

  const clearDateRangeFilter = () => {
    dispatch(
      updateGradebook({
        searchDateFrom: null,
        searchDateTo: null,
      }),
    );
    if (isGradebookV2Active) {
      dispatch(
        getGradebookInstructor(courseId, {
          searchDateFrom: null,
          searchDateTo: null,
        }),
      );
    } else {
      dispatch(getGradebook(courseId));
    }
  };

  // Exception Groups Filter
  const renderActiveExceptionGroupFilterButton = (filterOption) => {
    const { label, groupId } = filterOption;
    const activeFilter = exceptionGroups.some(
      (exceptionGroup) => exceptionGroup.groupId === groupId,
    );

    if (activeFilter) {
      return (
        <div
          className={styles.selectedFilterType}
          key={groupId}
          data-test-id={`active_exception_group_filter_${groupId.toLowerCase()}`}
        >
          <div className={styles.selectedFilterTypeText}>{label}</div>
          <MLButton
            className={styles.selectedFilterButton}
            testId={`remove_active_exception_group_filter_${groupId.toLowerCase()}`}
            onClick={() => {
              removeActiveExceptionGroupFilterButton(groupId);
            }}
          >
            <MLIcon type="x" className={styles.selectedFilterTypeIcon} />
          </MLButton>
        </div>
      );
    }
  };

  const removeActiveExceptionGroupFilterButton = (groupId) => {
    const updatedExceptionGroups = exceptionGroups.filter(
      (exceptionGroup) => exceptionGroup.groupId !== groupId,
    );

    handleExceptionGroupFilteredGradebook(updatedExceptionGroups);
  };

  const handleExceptionGroupFilteredGradebook = (updatedExceptionGroups) => {
    dispatch(setGradebookExceptionGroups(updatedExceptionGroups));
    if (isGradebookV2Active) {
      dispatch(
        getGradebookInstructor(courseId, {
          page: 1,
          exceptionGroupIds: updatedExceptionGroups.map((g) => g.groupId),
        }),
      );
    } else {
      dispatch(
        getGradebook(courseId, {
          page: 1,
          exceptionGroups: updatedExceptionGroups,
        }),
      );
    }
    dispatch(updateAllAverages());
  };

  // Resource Type Filter
  const renderActiveResourceTypeFilterButton = (filterOption) => {
    const { label, filters } = filterOption;
    const key = filters
      .map((filter) => `${filter.type}:${filter.value}`)
      .join(',');
    const activeFilter = isResourceTypeFilterOptionActive(
      resourceTypes,
      filters,
    );
    if (activeFilter) {
      return (
        <div
          className={styles.selectedFilterType}
          key={key}
          data-test-id={`active_filter_${label.toLowerCase()}`}
        >
          <div className={styles.selectedFilterTypeText}>{label}</div>
          <MLButton
            className={styles.selectedFilterButton}
            testId={`remove_active_filter_${label.toLowerCase()}`}
            onClick={() => {
              removeActiveResourceTypeFilterButton(filters);
            }}
          >
            <MLIcon type="x" className={styles.selectedFilterTypeIcon} />
          </MLButton>
        </div>
      );
    }
  };

  const removeActiveResourceTypeFilterButton = (filters) => {
    handleResourceTypeFilteredGradebook(
      removeResourceTypeFilters(resourceTypes, filters),
    );
  };

  const handleResourceTypeFilteredGradebook = (updatedResourceTypes) => {
    dispatch(setGradebookFilterTypes(updatedResourceTypes));
    if (isGradebookV2Active) {
      dispatch(
        getGradebookInstructor(courseId, {
          page: 1,
          filterTypes: updatedResourceTypes,
        }),
      );
    } else {
      dispatch(
        getGradebook(courseId, {
          page: 1,
          filterTypes: updatedResourceTypes,
        }),
      );
    }
  };

  return (
    <div
      className={styles.activeFilterHeaderContainer}
      ref={innerRef}
      data-test-id="filter_header"
    >
      <span>Active Filters:</span>
      {exceptionGroupOptions.map((filterOption) =>
        renderActiveExceptionGroupFilterButton(filterOption),
      )}
      {resourceTypeFilterOptions.map((filterOption) =>
        renderActiveResourceTypeFilterButton(filterOption),
      )}
      {searchDateTo && searchDateFrom && renderActiveDateRangeFilterButton()}
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a
        className={styles.clearAllFiltersBtn}
        role="button"
        tabIndex="0"
        data-test-id="clear_filters_button"
        onClick={() => {
          removeAllFilters();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            removeAllFilters();
          }
        }}
      >
        Clear All Filters
      </a>
    </div>
  );
}

FilterHeader.propTypes = {
  courseId: PropTypes.string,
  exceptionGroups: PropTypes.array,
  resourceTypes: PropTypes.array,
  searchDateFrom: PropTypes.object,
  searchDateTo: PropTypes.object,
  innerRef: PropTypes.func,
  isGradebookV2Active: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    courseId: courseIdSelector(state),
    exceptionGroups: exceptionGroupsSelector(state),
    resourceTypes: filterTypesSelector(state),
    searchDateFrom: searchDateFromSelector(state),
    searchDateTo: searchDateToSelector(state),
    isGradebookV2Active: isGradebookV2ActiveSelector(state),
  };
}

export default connect(mapStateToProps)(FilterHeader);
